import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Home, Building, Building2, Shield, CheckCircle, Users, PieChart, Trash2 } from 'lucide-react';

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Charte graphique ESS COPPAM
export const ESS_COLORS = {
  vertPrincipal: '#0A7F5A',
  bleuInstitutionnel: '#0B3C5D',
  sableClair: '#F4F6F3',
  grisStructurant: '#4B5563',
  statutActif: '#16A34A',
  statutSuspendu: '#F59E0B',
  statutExpire: '#DC2626'
};

// Configuration des territoires DOM - 3 actifs par défaut pour Sprint 1
export const TERRITOIRES_DOM = [
  { value: 'guadeloupe', label: 'Guadeloupe', emoji: '🌴', risk: 'Élevé', code: '971', deploiementInitial: true },
  { value: 'mayotte', label: 'Mayotte', emoji: '🏝️', risk: 'Très élevé', code: '976', deploiementInitial: true },
  { value: 'reunion', label: 'La Réunion', emoji: '🌋', risk: 'Élevé', code: '974', deploiementInitial: true },
  { value: 'martinique', label: 'Martinique', emoji: '🌺', risk: 'Élevé', code: '972', deploiementInitial: false },
  { value: 'guyane', label: 'Guyane', emoji: '🌳', risk: 'Très élevé', code: '973', deploiementInitial: false },
  { value: 'saint_martin', label: 'Saint-Martin', emoji: '🏖️', risk: 'Élevé', code: '978', deploiementInitial: false },
  { value: 'saint_barthelemy', label: 'Saint-Barthélemy', emoji: '⛵', risk: 'Élevé', code: '977', deploiementInitial: false }
];

export const TYPES_PROJET = [
  { value: 'individuel', label: 'Habitat individuel', icon: Home, description: 'Maison, auto-construction encadrée' },
  { value: 'collectif', label: 'Habitat collectif', icon: Building, description: 'Copropriété, immeuble' },
  { value: 'programme', label: 'Programme multi-lots', icon: Building2, description: 'Opérations groupées, lots multiples' }
];

export const STRUCTURES = [
  { value: 'beton', label: 'Béton' },
  { value: 'bois', label: 'Bois' },
  { value: 'mixte', label: 'Mixte' }
];

export const COMPLEXITE_SOL = [
  { value: 'simple', label: 'Simple', description: 'Terrain stable' },
  { value: 'moyen', label: 'Moyen', description: 'Quelques contraintes' },
  { value: 'complexe', label: 'Complexe', description: 'Terrain difficile' }
];

export const TYPES_LOT = [
  { value: 'gros_oeuvre', label: 'Gros œuvre', coeff: 1.5 },
  { value: 'charpente_couverture', label: 'Charpente / Couverture', coeff: 1.3 },
  { value: 'etancheite', label: 'Étanchéité', coeff: 1.4 },
  { value: 'menuiseries_exterieures', label: 'Menuiseries extérieures', coeff: 1.1 },
  { value: 'second_oeuvre', label: 'Second œuvre', coeff: 0.8 },
  { value: 'equipements', label: 'Équipements techniques', coeff: 0.7 },
  { value: 'espaces_verts', label: 'Espaces verts / VRD', coeff: 0.5 }
];

// Composant jauge de risque
export const RiskGauge = ({ score, niveau }) => {
  const getColor = () => {
    if (score <= 25) return { bg: 'bg-green-500', text: 'text-green-700', light: 'bg-green-100' };
    if (score <= 50) return { bg: 'bg-yellow-500', text: 'text-yellow-700', light: 'bg-yellow-100' };
    if (score <= 75) return { bg: 'bg-orange-500', text: 'text-orange-700', light: 'bg-orange-100' };
    return { bg: 'bg-red-500', text: 'text-red-700', light: 'bg-red-100' };
  };
  
  const colors = getColor();
  
  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium text-gray-600">Score de risque</span>
        <Badge className={`${colors.light} ${colors.text}`}>{niveau}</Badge>
      </div>
      <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full ${colors.bg} transition-all duration-500 rounded-full`}
          style={{ width: `${score}%` }}
        />
      </div>
      <div className="flex justify-between text-xs text-gray-400">
        <span>0</span>
        <span className="font-semibold text-gray-700">{score}/100</span>
        <span>100</span>
      </div>
    </div>
  );
};

// Composant affectation contribution
export const ContributionAllocation = ({ affectation }) => {
  const items = [
    { label: 'Fonds de garantie', value: affectation?.fonds_garantie || 0, percent: 60, color: 'bg-emerald-500', icon: Shield },
    { label: 'Prévention & expertise', value: affectation?.prevention_expertise || 0, percent: 25, color: 'bg-blue-500', icon: CheckCircle },
    { label: 'Gestion solidaire', value: affectation?.gestion_solidaire || 0, percent: 15, color: 'bg-purple-500', icon: Users }
  ];
  
  return (
    <div className="space-y-4">
      <h4 className="font-semibold text-gray-700 flex items-center gap-2">
        <PieChart className="h-4 w-4" />
        Affectation de la contribution
      </h4>
      <div className="grid grid-cols-3 gap-3">
        {items.map((item, idx) => (
          <div key={idx} className="bg-gray-50 rounded-lg p-3 text-center">
            <div className={`w-10 h-10 rounded-full ${item.color} mx-auto mb-2 flex items-center justify-center`}>
              <item.icon className="h-5 w-5 text-white" />
            </div>
            <div className="text-xl font-bold text-gray-800">{item.percent}%</div>
            <div className="text-sm font-semibold text-gray-700">{item.value.toLocaleString('fr-FR')} €</div>
            <div className="text-xs text-gray-500 mt-1">{item.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Composant formulaire lot (pour programmes multi-lots)
export const LotForm = ({ lot, index, onChange, onRemove }) => {
  return (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
      <div className="flex justify-between items-center mb-3">
        <h4 className="font-semibold text-gray-700">Lot {index + 1}</h4>
        <Button variant="ghost" size="sm" onClick={onRemove} className="text-red-500 hover:text-red-700">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Désignation</Label>
          <Input
            value={lot.designation}
            onChange={(e) => onChange({ ...lot, designation: e.target.value })}
            placeholder="Ex: Gros œuvre - Fondations"
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs">Type de lot</Label>
          <Select value={lot.type_lot} onValueChange={(v) => onChange({ ...lot, type_lot: v })}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPES_LOT.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label} (×{t.coeff})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Montant de base (€)</Label>
          <Input
            type="number"
            value={lot.montant_ht}
            onChange={(e) => onChange({ ...lot, montant_ht: parseFloat(e.target.value) || 0 })}
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs">Entreprise (optionnel)</Label>
          <Input
            value={lot.entreprise || ''}
            onChange={(e) => onChange({ ...lot, entreprise: e.target.value })}
            placeholder="Nom entreprise"
            className="mt-1"
          />
        </div>
      </div>
    </div>
  );
};
